import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, UrlSerializer } from '@angular/router';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { MissingTranslationHandler, TranslateCompiler, TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { AUTH_SERVICE, AuthModule } from 'ngx-auth';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { DynamicHooksModule } from 'ngx-dynamic-hooks';
import { provideNgxMask } from 'ngx-mask';
import { MatomoModule } from 'ngx-matomo-client';
import { QuillModule } from 'ngx-quill';
import { ToastrModule } from 'ngx-toastr';
import { ANALYTICS_SERVICE_TOKEN, AUTH_DATA_SERVICE_TOKEN, LOCALIZED_TOASTER_SERVICE_TOKEN, LOGGER_TOKEN, MODAL_SERVICE_TOKEN, MUSIC_SERVICE_TOKEN, QUESTION_EVENTS_SERVICE_TOKEN, STUDENT_MUSIC_SERVICE_TOKEN } from 'shared-components';
import {
  TranslateMessageFormatCompiler,
  MESSAGE_FORMAT_CONFIG
} from 'ngx-translate-messageformat-compiler';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthenticationDataService, AuthenticationService } from './core/authentication';
import { NgxZendeskWebwidgetModule } from './core/chat/zendesk-webwidget/zendesk-webwidget.module';
import { CoreModule } from './core/core.module';
import { ExceptionService } from './core/exceptions/exception.service';
import { ClientVersionInterceptor } from './core/http/client-version.interceptor';
import { ExcludeHeaderRequestInterceptor } from './core/http/exclude-header-request.interceptor';
import { RequestInterceptor } from './core/http/request.interceptor';
import { CustomMissingTranslationHandler } from './core/internationalization/custom-missing-translation-handler';
import { Logger } from './core/logger/services/logger.service';
import { ModalService } from './core/modals/services/modal.service';
import { CustomRouteReuseStrategy } from './core/navigation/custom-route-reuse-strategy';
import { LowerCaseUrlSerializer } from './core/navigation/lower-case-url-serializer';
import { LocalizedToasterService } from './core/toaster/localized-toaster.service';
import { ZendeskConfig } from './core/zendesk-config/zendesk-config';
import { AuthConfigModule } from './partner/auth-config-json.module';
import { QuestionEventsService } from './question/components/question/question-events.service';
import { CloudflareTimeService } from './shared/services/cloudflare-time-service';
import { KhAnalyticsService } from './shared/services/kh-analytics.service';
import { MESSAGE_BUS_SERVICE } from './shared/services/message-bus/message-bus-service.interface';
import { RTCSignalRStrategyService } from './shared/services/message-bus/rtc-signalr-strategy.service';
import { MusicService } from './shared/services/music.service';
import { SERVER_TIME_TOKEN } from './shared/services/server-time.interface';
import { StudentMusicService } from './student/services/student-music.service';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { environment } from 'env/environment';

export function initTranslationsFactory(
  translationService: TranslationService
) {
  return async () => {
    await translationService.initializeTranslations();
  };
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', `.json?v=${environment.clientVersion}`);
}

@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent
  ],
  imports: [
    AuthModule,
    CoreModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BsDropdownModule.forRoot(),
    TypeaheadModule.forRoot(),
    CollapseModule.forRoot(),
    ToastrModule.forRoot({
      closeButton: true,
      timeOut: 4000
    }),
    TooltipModule.forRoot(),
    BsDatepickerModule.forRoot(),
    LoadingBarHttpClientModule,
    LoadingBarModule,
    DynamicHooksModule.forRoot({}),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CustomMissingTranslationHandler,
        deps: [Injector]
      }
    }),
    NgxZendeskWebwidgetModule.forRoot(ZendeskConfig),
    PaginationModule.forRoot(),
    QuillModule.forRoot(),
    MatomoModule.forRoot({
      disabled: environment.MatomoSiteId === '',
      scriptUrl: environment.MatomoUrl + 'ma.js',
      siteId: environment.MatomoSiteId,
      trackerUrl: environment.MatomoUrl,
      trackerUrlSuffix: 'ma.ph',
      runOutsideAngularZone: true
    }),
    AuthConfigModule
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideNgxMask(),
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslationsFactory,
      deps: [TranslationService],
      multi: true,
    },
    {
      provide: MESSAGE_FORMAT_CONFIG,
      useValue: {
        throwOnError: true
      }
    },
    { provide: AUTH_SERVICE, useClass: AuthenticationService },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: ClientVersionInterceptor },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: ExcludeHeaderRequestInterceptor },
    { provide: UrlSerializer, useClass: LowerCaseUrlSerializer },
    BsDatepickerConfig,
    environment.disableLogging ? [] : { provide: ErrorHandler, useClass: ExceptionService },
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    { provide: MESSAGE_BUS_SERVICE, useClass: RTCSignalRStrategyService },
    { provide: QUESTION_EVENTS_SERVICE_TOKEN, useExisting: QuestionEventsService },
    { provide: ANALYTICS_SERVICE_TOKEN, useExisting: KhAnalyticsService },
    { provide: AUTH_DATA_SERVICE_TOKEN, useExisting: AuthenticationDataService },
    { provide: LOCALIZED_TOASTER_SERVICE_TOKEN, useExisting: LocalizedToasterService },
    environment.disableLogging ? [] : { provide: LOGGER_TOKEN, useExisting: Logger },
    { provide: MODAL_SERVICE_TOKEN, useExisting: ModalService },
    { provide: MUSIC_SERVICE_TOKEN, useExisting: MusicService },
    { provide: STUDENT_MUSIC_SERVICE_TOKEN, useExisting: StudentMusicService },
    { provide: SERVER_TIME_TOKEN, useClass: CloudflareTimeService }
  ]
})
export class AppModule { }

